import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import {
  fileExceedsMaxUploadSize,
  maxUploadSizeExceededMessage,
} from "../utils/uploadFileSizeLimit";
import {
  REGISTRATION_OPTIONS,
  getAccountTypesForRegistration,
} from "../constants/accountConstants";
import {
  TITLE_OPTIONS,
  RELATIONSHIP_OPTIONS,
} from "../constants/membersConstants";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mkLocalKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `k_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function ownershipLimitsForRegistration(registration) {
  const reg = registration || "";
  if (reg === "Joint" || reg === "UTMA/UGMA") return { min: 2, max: 2 };
  if (
    ["Individual", "529"].includes(reg) ||
    reg.includes("Retirement") ||
    reg.includes("IRA")
  ) {
    return { min: 1, max: 1 };
  }
  if (reg === "Business") return { min: 1, max: 4 };
  if (reg === "Trust" || reg === "LLC") return { min: 1, max: 2 };
  return { min: 1, max: 2 };
}

function defaultTypeForRegistration(registration) {
  const types = getAccountTypesForRegistration(registration);
  return types.length === 1 ? types[0] : "";
}

function generateUUID() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const defaultMembersState = () => [
  {
    localKey: mkLocalKey(),
    title: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    is_primary: true,
    relationship: "",
  },
];

const defaultAccountsState = () => [];

/**
 * Create New Households: bulk Excel (doc) or single manual household with members + accounts.
 */
function CreateHouseholdsModal({
  isOpen,
  onClose,
  project,
  projectId,
  onSuccess,
}) {
  const { toast } = useToast();
  const [tab, setTab] = useState("doc");
  const [advisorId, setAdvisorId] = useState("");
  const [docUploading, setDocUploading] = useState(false);
  const [docDragging, setDocDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [householdName, setHouseholdName] = useState("");
  const [members, setMembers] = useState(defaultMembersState);
  const [accounts, setAccounts] = useState(defaultAccountsState);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualError, setManualError] = useState("");

  const hasAdvisors = project?.advisors && project.advisors.length > 0;

  const resetAll = useCallback(() => {
    setTab("doc");
    setAdvisorId("");
    setDocUploading(false);
    setDocDragging(false);
    setSelectedFile(null);
    setHouseholdName("");
    setMembers(defaultMembersState());
    setAccounts(defaultAccountsState());
    setManualSubmitting(false);
    setManualError("");
  }, []);

  useEffect(() => {
    if (isOpen) resetAll();
  }, [isOpen, resetAll]);

  const memberDisplay = useCallback((m, i) => {
    const t = (m.title || "").trim();
    const fn = (m.first_name || "").trim() || "First name";
    const ln = (m.last_name || "").trim() || "Last name";
    const prefix = t ? `${t} ` : "";
    return `${prefix}${fn} ${ln}`.trim() || `Member ${i + 1}`;
  }, []);

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleSelectCreateHouseholdFile = (filesList) => {
    const files = Array.from(filesList || []).filter(Boolean);
    if (!files.length) return;
    if (files.length > 1) {
      toast.error("Please upload only one file at a time.");
      return;
    }

    const createFile = files[0];
    const lowerName = createFile.name.toLowerCase();
    if (!lowerName.endsWith(".xlsx") && !lowerName.endsWith(".xls")) {
      toast.error("Only Excel files (.xlsx, .xls) are allowed.");
      return;
    }

    if (fileExceedsMaxUploadSize(createFile)) {
      toast.error(maxUploadSizeExceededMessage(createFile));
      return;
    }

    setSelectedFile(createFile);
  };

  const handleUploadSelectedFile = async () => {
    if (!selectedFile) return;

    if (hasAdvisors && !advisorId) {
      toast.error("Please select an advisor for this upload.");
      return;
    }

    setDocUploading(true);

    try {
      const file = selectedFile;
      const batchId = generateUUID();

      const presignedResponse = await axiosInstance.post(
        `/api/files/presigned-urls`,
        {
          file_names: [file.name],
          batch_id: batchId,
          project_id: projectId,
          advisor_id: advisorId,
          upload_type: "aggregated_households_upload",
        },
      );

      const { presigned_urls } = presignedResponse.data;
      if (!presigned_urls || presigned_urls.length === 0) {
        throw new Error("No presigned URL received from server");
      }

      const presignedUrlData = presigned_urls[0];

      const contentType = file.name.toLowerCase().endsWith(".xlsx")
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/vnd.ms-excel";

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });
        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });
        xhr.open("PUT", presignedUrlData.presigned_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
      });

      const confirmResponse = await axiosInstance.post(`/api/files/confirm-upload`, {
        project_id: projectId,
        batch_id: batchId,
        upload_type: "aggregated_households_upload",
      });

      handleClose();
      if (onSuccess) onSuccess();
      toast.success("File uploaded and is being processed in the background");
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error(humanizeError(error));
    } finally {
      setDocUploading(false);
    }
  };

  const handleDocFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleSelectCreateHouseholdFile(files);
    }
    e.target.value = "";
  };

  const setPrimaryMember = (localKey) => {
    setMembers((prev) =>
      prev.map((m) => ({
        ...m,
        is_primary: m.localKey === localKey,
        relationship: m.localKey === localKey ? "" : m.relationship,
      })),
    );
  };

  const addMember = () => {
    setMembers((prev) => [
      ...prev,
      {
        localKey: mkLocalKey(),
        title: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        is_primary: false,
        relationship: "",
      },
    ]);
  };

  const removeMember = (localKey) => {
    setMembers((prev) => {
      if (prev.length <= 1) return prev;
      const idx = prev.findIndex((m) => m.localKey === localKey);
      if (idx < 0) return prev;
      const wasPrimary = prev[idx].is_primary;
      const next = prev.filter((m) => m.localKey !== localKey);
      if (wasPrimary && next.length) {
        next[0] = { ...next[0], is_primary: true, relationship: "" };
      }
      const removedIdx = idx;
      setAccounts((accs) =>
        accs.map((a) => {
          const nextIdx = a.ownerIndices
            .filter((i) => i !== removedIdx)
            .map((i) => (i > removedIdx ? i - 1 : i));
          const lim = ownershipLimitsForRegistration(a.registration);
          let fixed = nextIdx;
          if (fixed.length > lim.max) {
            fixed = fixed.slice(0, lim.max);
          }
          if (fixed.length < lim.min && next.length) {
            fixed = Array.from(
              { length: Math.min(lim.min, next.length) },
              (_, j) => j,
            );
          }
          return { ...a, ownerIndices: fixed };
        }),
      );
      return next;
    });
  };

  const addAccount = () => {
    setAccounts((prev) => [
      ...prev,
      {
        localKey: mkLocalKey(),
        registration: "Individual",
        type: defaultTypeForRegistration("Individual"),
        ownerIndices: [0],
      },
    ]);
  };

  const removeAccount = (localKey) => {
    setAccounts((prev) => prev.filter((a) => a.localKey !== localKey));
  };

  const toggleAccountOwner = (accountKey, memberIndex, registration) => {
    const lim = ownershipLimitsForRegistration(registration);
    setAccounts((prev) =>
      prev.map((a) => {
        if (a.localKey !== accountKey) return a;
        const has = a.ownerIndices.includes(memberIndex);
        let next = has
          ? a.ownerIndices.filter((i) => i !== memberIndex)
          : [...a.ownerIndices, memberIndex].sort((x, y) => x - y);
        if (!has && next.length > lim.max) {
          next = next.slice(0, lim.max);
        }
        if (next.length === 0) {
          next = [memberIndex];
        }
        return { ...a, ownerIndices: next };
      }),
    );
  };

  const validateManual = () => {
    if (hasAdvisors && !advisorId) {
      return "Please select an advisor.";
    }
    if (!householdName.trim()) {
      return "Household name is required.";
    }
    const primaryCount = members.filter((m) => m.is_primary).length;
    if (primaryCount !== 1) {
      return "Select exactly one primary owner.";
    }
    for (let i = 0; i < members.length; i += 1) {
      const m = members[i];
      if (!(m.first_name || "").trim() || !(m.last_name || "").trim()) {
        return `${memberDisplay(m, i)}: first and last name are required.`;
      }
      if (!m.is_primary && !(m.relationship || "").trim()) {
        return `${memberDisplay(m, i)}: relationship to primary owner is required.`;
      }
    }
    if (accounts.length > 0) {
      for (let i = 0; i < accounts.length; i += 1) {
        const a = accounts[i];
        const lim = ownershipLimitsForRegistration(a.registration);
        if (!a.registration) {
          return `Account ${i + 1}: registration type is required.`;
        }
        if (getAccountTypesForRegistration(a.registration).length > 1 && !(a.type || "").trim()) {
          return `Account ${i + 1}: account type is required.`;
        }
        if (lim.min > members.length) {
          return `Account ${i + 1}: ${a.registration} requires ${lim.min} account owner(s), but only ${members.length} household member(s) are listed. Add more members first.`;
        }
        if (a.ownerIndices.length < lim.min) {
          return `Account ${i + 1}: ${a.registration} requires exactly ${lim.min} account owner(s). Select ${lim.min - a.ownerIndices.length} more.`;
        }
        if (a.ownerIndices.length > lim.max) {
          return `Account ${i + 1}: ${a.registration} allows at most ${lim.max} account owner(s).`;
        }
      }
    }
    return null;
  };

  const submitManual = async () => {
    const err = validateManual();
    if (err) {
      setManualError(err);
      return;
    }
    setManualError("");
    setManualSubmitting(true);
    try {
      const payload = {
        name: householdName.trim(),
        advisor_id: hasAdvisors ? advisorId : undefined,
        members: members.map((m) => ({
          title: (m.title || "").trim() || null,
          first_name: m.first_name.trim(),
          last_name: m.last_name.trim(),
          email: (m.email || "").trim() || null,
          mobile_number: (m.phone || "").trim() || null,
          is_primary: m.is_primary,
          relationship_to_primary: m.is_primary
            ? null
            : (m.relationship || "").trim() || null,
        })),
        accounts: accounts.map((a) => ({
          registration: a.registration,
          type: (a.type || "").trim() || null,
          owner_member_indices: [...a.ownerIndices],
        })),
      };

      await axiosInstance.post(
        `/households/project/${projectId}/manual`,
        payload,
      );
      if (onSuccess) onSuccess();
      handleClose();
      toast.success("Household created.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map((x) => x.msg || JSON.stringify(x)).join("; ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = e.message || "Failed to create household.";
      }
      setManualError(msg);
    } finally {
      setManualSubmitting(false);
    }
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-bold transition-all ${
        tab === id
          ? "bg-white text-emerald-800 shadow-sm ring-1 ring-emerald-200"
          : "text-gray-500 hover:text-gray-800"
      }`}
    >
      {label}
    </button>
  );

  const manualValidationError =
    tab === "manual" ? validateManual() : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] overflow-y-auto ${
          tab === "manual" ? "max-w-3xl" : "max-w-2xl"
        }`}
      >
        <div className="p-6 relative">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-emerald-700 bg-clip-text text-transparent flex-1">
              Create New Households
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {hasAdvisors && (
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Select advisor *
              </label>
              <select
                value={advisorId}
                onChange={(e) => setAdvisorId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base"
              >
                <option value="">-- Select advisor --</option>
                {project.advisors.map((advisor, index) => (
                  <option
                    key={index}
                    value={advisor.user_id || String(index)}
                  >
                    {advisor.first_name} {advisor.last_name}{" "}
                    {advisor.g_number && `(G#${advisor.g_number})`}{" "}
                    {advisor.rr_code && `(RR:${advisor.rr_code})`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-5 flex rounded-xl bg-gray-100 p-1 gap-1">
            {tabBtn("doc", "Document upload")}
            {tabBtn("manual", "Manual entry")}
          </div>

          {tab === "doc" && (
            <>
              <div className="mb-5 rounded-xl border border-emerald-200/80 bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 px-4 py-3.5 shadow-sm">
                <div className="flex gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      Household master sheet
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">
                      Upload a household master sheet with at least{" "}
                      <span className="font-medium text-gray-800">
                        household name
                      </span>
                      ,{" "}
                      <span className="font-medium text-gray-800">
                        owner names
                      </span>
                      , and{" "}
                      <span className="font-medium text-gray-800">
                        account types
                      </span>
                      .
                    </p>
                  </div>
                </div>
              </div>

              {!selectedFile && (
              <label className="block">
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDocDragging(false);
                    const { files } = e.dataTransfer;
                    if (files && files.length > 0) {
                      handleSelectCreateHouseholdFile(files);
                    }
                  }}
                  className={`mt-2 flex justify-center px-4 pt-6 pb-6 border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer ${
                    docDragging
                      ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/50 shadow-lg scale-[1.02]"
                      : "border-emerald-200 hover:border-emerald-400 bg-gradient-to-br from-gray-50/50 to-emerald-50/30 hover:shadow-md"
                  }`}
                >
                  <div className="space-y-2 text-center">
                    <div className="mx-auto w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                      <svg
                        className="h-6 w-6 text-white"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="flex text-sm text-gray-700 font-medium justify-center items-center gap-2">
                      <span className="relative cursor-pointer font-bold text-emerald-600 hover:text-emerald-700 transition-colors">
                        Upload file
                      </span>
                      <span className="text-gray-500">or</span>
                      <span className="text-gray-600">drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-500 font-medium">
                      Excel (.xlsx, .xls)
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleDocFileInputChange}
                    disabled={docUploading}
                    className="hidden"
                  />
                </div>
              </label>
              )}

              {selectedFile && (
                <div className="mt-2 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(selectedFile.size)} · Ready to upload
                      </p>
                    </div>
                    {!docUploading && (
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        aria-label="Remove file"
                        className="flex-shrink-0 text-gray-500 hover:text-gray-700"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="mt-4 flex justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      disabled={docUploading}
                      className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Choose different file
                    </button>
                    <button
                      type="button"
                      onClick={handleUploadSelectedFile}
                      disabled={docUploading}
                      className="px-6 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {docUploading ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Uploading…
                        </>
                      ) : (
                        "Upload & process"
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "manual" && (
            <div className="space-y-6">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 px-4 py-3 text-sm text-gray-700">
                <span className="font-semibold text-emerald-900">
                  Manual entry
                </span>{" "}
                creates one household with the owners and accounts below. You
                can add more accounts later from the household record.
              </div>

              {manualError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {manualError}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Household name *
                </label>
                <input
                  type="text"
                  value={householdName}
                  onChange={(e) => setHouseholdName(e.target.value)}
                  placeholder="e.g. Smith Family"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-base"
                />
              </div>

              <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                    Household members
                  </h3>
                  <button
                    type="button"
                    onClick={addMember}
                    className="text-sm font-bold text-emerald-600 hover:text-emerald-700"
                  >
                    + Add member
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Include everyone who will appear as an account owner. One
                  person must be the primary owner; other owners need their
                  relationship to that person.
                </p>
                <div className="space-y-4">
                  {members.map((m, index) => (
                    <div
                      key={m.localKey}
                      className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 space-y-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-gray-700">
                          <input
                            type="radio"
                            name="primary-owner"
                            checked={m.is_primary}
                            onChange={() => setPrimaryMember(m.localKey)}
                            className="text-emerald-600 focus:ring-emerald-500"
                          />
                          Primary owner
                        </label>
                        {members.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeMember(m.localKey)}
                            className="text-xs font-semibold text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Title
                          </label>
                          <select
                            value={m.title}
                            onChange={(e) => {
                              const v = e.target.value;
                              setMembers((prev) =>
                                prev.map((x) =>
                                  x.localKey === m.localKey
                                    ? { ...x, title: v }
                                    : x,
                                ),
                              );
                            }}
                            className="w-full h-12 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="">—</option>
                            {TITLE_OPTIONS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-5">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            First name *
                          </label>
                          <input
                            value={m.first_name}
                            onChange={(e) =>
                              setMembers((prev) =>
                                prev.map((x) =>
                                  x.localKey === m.localKey
                                    ? { ...x, first_name: e.target.value }
                                    : x,
                                ),
                              )
                            }
                            className="w-full min-h-[48px] h-12 px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="md:col-span-5">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Last name *
                          </label>
                          <input
                            value={m.last_name}
                            onChange={(e) =>
                              setMembers((prev) =>
                                prev.map((x) =>
                                  x.localKey === m.localKey
                                    ? { ...x, last_name: e.target.value }
                                    : x,
                                ),
                              )
                            }
                            className="w-full min-h-[48px] h-12 px-4 py-3 border border-gray-200 rounded-lg text-base focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        {!m.is_primary && (
                          <div className="md:col-span-12">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Relationship to primary *
                            </label>
                            <select
                              value={m.relationship}
                              onChange={(e) =>
                                setMembers((prev) =>
                                  prev.map((x) =>
                                    x.localKey === m.localKey
                                      ? {
                                          ...x,
                                          relationship: e.target.value,
                                        }
                                      : x,
                                  ),
                                )
                              }
                              className="w-full max-w-md h-12 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500"
                            >
                              <option value="">Select…</option>
                              {RELATIONSHIP_OPTIONS.map((r) => (
                                <option key={r} value={r}>
                                  {r}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Email{" "}
                            <span className="text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="email"
                            value={m.email}
                            onChange={(e) =>
                              setMembers((prev) =>
                                prev.map((x) =>
                                  x.localKey === m.localKey
                                    ? { ...x, email: e.target.value }
                                    : x,
                                ),
                              )
                            }
                            className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Phone{" "}
                            <span className="text-gray-400">(optional)</span>
                          </label>
                          <input
                            type="tel"
                            value={m.phone}
                            onChange={(e) =>
                              setMembers((prev) =>
                                prev.map((x) =>
                                  x.localKey === m.localKey
                                    ? { ...x, phone: e.target.value }
                                    : x,
                                ),
                              )
                            }
                            className="w-full h-10 px-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                    Accounts
                  </h3>
                  <button
                    type="button"
                    onClick={addAccount}
                    className="text-sm font-bold text-emerald-600 hover:text-emerald-700"
                  >
                    + Add account
                  </button>
                </div>
                <div className="space-y-4">
                  {accounts.map((a, ai) => {
                    const lim = ownershipLimitsForRegistration(a.registration);
                    return (
                      <div
                        key={a.localKey}
                        className="rounded-xl border border-emerald-100/80 bg-gradient-to-br from-white to-emerald-50/30 p-4 space-y-4"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-sm font-semibold text-gray-800">
                            Account {ai + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAccount(a.localKey)}
                            className="text-xs font-semibold text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="max-w-md">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Registration type *
                          </label>
                          <select
                            value={a.registration}
                            onChange={(e) => {
                              const reg = e.target.value;
                              setAccounts((prev) =>
                                prev.map((x) => {
                                  if (x.localKey !== a.localKey) return x;
                                  const L = ownershipLimitsForRegistration(reg);
                                  let idx = [...x.ownerIndices];
                                  if (idx.length > L.max) {
                                    idx = idx.slice(0, L.max);
                                  }
                                  if (idx.length < L.min) {
                                    const n = members.length;
                                    const pick = Math.min(L.min, n);
                                    idx = Array.from({ length: pick }, (_, j) => j);
                                  }
                                  return {
                                    ...x,
                                    registration: reg,
                                    type: defaultTypeForRegistration(reg),
                                    ownerIndices: idx,
                                  };
                                }),
                              );
                            }}
                            className="w-full h-11 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500"
                          >
                            {REGISTRATION_OPTIONS.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </div>
                        {getAccountTypesForRegistration(a.registration).length >
                          1 && (
                          <div className="max-w-md">
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Account type *
                            </label>
                            <select
                              value={a.type || ""}
                              onChange={(e) => {
                                const t = e.target.value;
                                setAccounts((prev) =>
                                  prev.map((x) =>
                                    x.localKey === a.localKey
                                      ? { ...x, type: t }
                                      : x,
                                  ),
                                );
                              }}
                              className="w-full h-11 px-3 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500"
                            >
                              <option value="">Select account type</option>
                              {getAccountTypesForRegistration(a.registration).map(
                                (t) => (
                                  <option key={t} value={t}>
                                    {t}
                                  </option>
                                ),
                              )}
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-2">
                            Account owners * (
                            {lim.min === lim.max
                              ? `exactly ${lim.min}`
                              : `${lim.min}–${lim.max}`}{" "}
                            for {a.registration})
                          </label>
                          {lim.min > members.length && (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                              This registration needs {lim.min} owner
                              {lim.min !== 1 ? "s" : ""}, but the household only
                              has {members.length} member
                              {members.length !== 1 ? "s" : ""}. Add another member
                              above.
                            </p>
                          )}
                          {lim.min <= members.length &&
                            a.ownerIndices.length < lim.min && (
                              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
                                Select {lim.min - a.ownerIndices.length} more
                                owner
                                {lim.min - a.ownerIndices.length !== 1
                                  ? "s"
                                  : ""}{" "}
                                for this account.
                              </p>
                            )}
                          <div className="flex flex-wrap gap-2">
                            {members.map((m, mi) => {
                              const checked = a.ownerIndices.includes(mi);
                              const atMaxPick =
                                !checked &&
                                a.ownerIndices.length >= lim.max;
                              return (
                                <label
                                  key={m.localKey}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                                    atMaxPick
                                      ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                                      : checked
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-medium cursor-pointer"
                                        : "border-gray-200 bg-white text-gray-600 hover:border-emerald-200 cursor-pointer"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={atMaxPick}
                                    onChange={() =>
                                      toggleAccountOwner(
                                        a.localKey,
                                        mi,
                                        a.registration,
                                      )
                                    }
                                    className="rounded text-emerald-600 focus:ring-emerald-500 disabled:opacity-40"
                                  />
                                  {memberDisplay(m, mi)}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

                <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitManual}
                  disabled={
                    manualSubmitting || manualValidationError !== null
                  }
                  title={manualValidationError || undefined}
                  className="px-8 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-md shadow-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {manualSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating…
                    </>
                  ) : (
                    "Create household"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateHouseholdsModal;
